// eslint-disable-next-line simple-import-sort/imports
import { MapBrowserEvent } from "ol";
import Overlay from "ol/Overlay";
import { useContext, useState } from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";

import { ColorConstants } from "../../../../constants";
import { LocationAndGisFileData } from "../../../../models";
import { splitCoordinates, toCoordinate } from "../../../../utils/location";
import { LandholdingContext } from "../LandholdingContext";

interface UseLocationReturnData {
  locationData: LocationAndGisFileData[];
  hoverOverlay?: Overlay;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pointermoveFunc?: (event: MapBrowserEvent<any>) => void;
  projectCoordinates: { lat: number; lng: number }[];
  hasMappableLocation: boolean;
}

export const useLocation = (): UseLocationReturnData => {
  const { landholdingDetails } = useContext(LandholdingContext);
  const [locationData, setLocationData] = useState<LocationAndGisFileData[]>([]);
  const [projectCoordinates, setProjectCoordinates] = useState<{ lat: number; lng: number }[]>([]);
  const overLayId = "info-overlay";
  const infoElement = document.createElement("div");
  infoElement.className = "info-overlay";
  const hoverOverlay = new Overlay({
    id: overLayId,
    element: infoElement,
    positioning: "bottom-left",
    stopEvent: false,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pointermoveFunc = (event: MapBrowserEvent<any>): void => {
    const infoDiv = event.map.forEachFeatureAtPixel(event.pixel, (f, l) => {
      // eslint-disable-next-line no-restricted-syntax
      const infoOverlay = event.map.getOverlayById(overLayId);
      const projectName = l.get("projectName");
      const areaNetHa = l.get("areaNetHa");
      if (projectName) {
        if (!infoOverlay) return null;
        infoOverlay.setPosition(event.coordinate);
        infoElement.innerHTML = `<h4>${projectName}</h4><p>${areaNetHa} ha</p>`;
        infoElement.style.display = "block";
        infoElement.style.width = "max-content";
        return infoElement;
      }
      return null;
    });
    if (!infoDiv) {
      infoElement.style.display = "none";
    }
  };

  useDeepCompareEffectNoCheck(() => {
    setLocationData(
      landholdingDetails?.projects.map((projectDetails, index) => ({
        addressCountryCode: projectDetails?.addressCountryCode,
        gridReference: projectDetails?.locationGridReference,
        ...splitCoordinates(projectDetails?.locationCoordinates),
        locationUuid: projectDetails?.projectLocation?.uuid ?? null,
        locationRowVersion: projectDetails?.projectLocation?.rowVersion ?? null,
        boundaryData: projectDetails?.projectLocation?.boundaryData ?? null,
        shapeFileUrl: projectDetails?.projectLocation?.locationFileUrl ?? undefined,
        gisFile: {
          projectShape: {
            geoJsonFileUrl: projectDetails?.projectLocation?.locationFileUrl ?? undefined,
            geoJsonContent: projectDetails?.projectLocation?.boundaryData,
            fillColor: ColorConstants.CHART_COLORS[index % ColorConstants.CHART_COLORS.length],
          },
          vectorFile: undefined,
          rasterLayer: undefined,
          extraInfo: {
            projectName: projectDetails?.name,
            areaNetHa: typeof projectDetails.areaNetHa === "number" ? projectDetails.areaNetHa : undefined,
          },
        },
      })) ?? []
    );
    const coordinatesList: { lat: number; lng: number }[] = [];
    landholdingDetails?.projects.forEach((projectDetails) => {
      const coordinate = toCoordinate(projectDetails?.locationCoordinates);
      if (coordinate) {
        coordinatesList.push(coordinate);
      }
    });
    setProjectCoordinates(coordinatesList);
  }, [landholdingDetails]);

  /* A landholding always has projects, but they need not have anywhere to put on a map. Without
     something to frame the map has no view at all, so the empty state is the honest answer. */
  const hasMappableLocation =
    projectCoordinates.length > 0 ||
    locationData.some(({ gisFile }) => gisFile.projectShape.geoJsonContent || gisFile.projectShape.geoJsonFileUrl);

  return {
    locationData,
    hoverOverlay,
    pointermoveFunc,
    projectCoordinates,
    hasMappableLocation,
  };
};
